import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useWorkspace } from "../app/workspace";
import { usd, num, relDate, shortDate } from "../lib/format";
import type { HealthBand } from "../lib/types";
import { Icon } from "../components/icons";
import { HealthRing, Sparkline, Avatar, Monogram } from "../components/ui";

type BandFilter = "all" | HealthBand;
type Region = "all" | "NA" | "EMEA" | "APAC";
type SortKey = "health" | "arr" | "renewal";

const bandChips: { key: BandFilter; label: string }[] = [
  { key: "all", label: "All" },
  { key: "good", label: "Healthy" },
  { key: "watch", label: "Watch" },
  { key: "risk", label: "At risk" },
];

const regions: Region[] = ["all", "NA", "EMEA", "APAC"];
const sorts: { key: SortKey; label: string }[] = [
  { key: "health", label: "Health" },
  { key: "arr", label: "ARR" },
  { key: "renewal", label: "Renewal date" },
];

type SavedView = { id: string; name: string; band: BandFilter; region: Region; sort: SortKey; query: string };
const VIEWS_KEY = "penumbrai:accountViews";
const BUILTIN_VIEWS: SavedView[] = [
  { id: "v_atrisk", name: "At-risk book", band: "risk", region: "all", sort: "arr", query: "" },
  { id: "v_renew", name: "Renewing soon", band: "all", region: "all", sort: "renewal", query: "" },
];
const loadViews = (): SavedView[] => { try { const r = localStorage.getItem(VIEWS_KEY); if (r) return JSON.parse(r); } catch { /* ignore */ } return BUILTIN_VIEWS; };
const saveViews = (v: SavedView[]) => { try { localStorage.setItem(VIEWS_KEY, JSON.stringify(v)); } catch { /* ignore */ } };
const uid = (p: string) => `${p}_${Date.now().toString(36)}${Math.floor(Math.random() * 1e3).toString(36)}`;

export default function Accounts() {
  const { ws, userById } = useWorkspace();
  const navigate = useNavigate();

  const [query, setQuery] = useState("");
  const [band, setBand] = useState<BandFilter>("all");
  const [region, setRegion] = useState<Region>("all");
  const [sort, setSort] = useState<SortKey>("health");
  const [views, setViews] = useState<SavedView[]>(loadViews);
  const [activeView, setActiveView] = useState<string>("");

  const applyView = (v: SavedView) => { setBand(v.band); setRegion(v.region); setSort(v.sort); setQuery(v.query); setActiveView(v.id); };
  const saveCurrentView = () => {
    const name = window.prompt("Name this view", "My view")?.trim();
    if (!name) return;
    const v: SavedView = { id: uid("v"), name, band, region, sort, query };
    const next = [...views, v]; setViews(next); saveViews(next); setActiveView(v.id);
  };
  const deleteView = (id: string) => { const next = views.filter((v) => v.id !== id); setViews(next); saveViews(next); if (activeView === id) setActiveView(""); };
  // Any manual filter change drops the "active saved view" highlight.
  const clearActive = () => setActiveView("");

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = ws.accounts.filter((a) => {
      if (band !== "all" && a.healthBand !== band) return false;
      if (region !== "all" && a.region !== region) return false;
      if (q && !a.name.toLowerCase().includes(q) && !a.domain.toLowerCase().includes(q)) return false;
      return true;
    });
    const sorted = [...filtered].sort((a, b) => {
      if (sort === "arr") return b.arr - a.arr;
      if (sort === "renewal") return +new Date(a.renewalDate) - +new Date(b.renewalDate);
      return a.health - b.health; // health: most urgent first
    });
    return sorted;
  }, [ws.accounts, query, band, region, sort]);

  const totalArr = useMemo(() => rows.reduce((s, a) => s + a.arr, 0), [rows]);

  return (
    <div className="page">
      <div className="mb-24">
        <p className="eyebrow">Accounts</p>
        <h1 style={{ fontFamily: "var(--serif)", fontSize: 28, marginTop: 6 }}>Book of business</h1>
        <p className="muted" style={{ marginTop: 4 }}>
          Every account you steward, brought into focus — filter by health, region, and renewal to find where attention is owed.
        </p>
      </div>

      <div className="toolbar">
        <div className="search" style={{ marginLeft: 0, minWidth: 260 }}>
          <Icon name="search" size={15} />
          <input
            className="input"
            style={{ border: "none", background: "transparent", padding: 0, flex: 1 }}
            placeholder="Search accounts…"
            value={query}
            onChange={(e) => { setQuery(e.target.value); clearActive(); }}
          />
        </div>

        <div className="row wrap" style={{ gap: 6 }}>
          {bandChips.map((c) => (
            <button
              key={c.key}
              className={`chip ${band === c.key ? "chip--active" : ""}`}
              style={{ cursor: "pointer" }}
              onClick={() => { setBand(c.key); clearActive(); }}
            >
              {c.label}
            </button>
          ))}
        </div>

        <div className="seg-control" style={{ marginLeft: "auto" }}>
          {regions.map((r) => (
            <button key={r} className={region === r ? "on" : ""} onClick={() => { setRegion(r); clearActive(); }}>
              {r === "all" ? "All regions" : r}
            </button>
          ))}
        </div>

        <div className="seg-control">
          {sorts.map((s) => (
            <button key={s.key} className={sort === s.key ? "on" : ""} onClick={() => { setSort(s.key); clearActive(); }}>
              {s.label}
            </button>
          ))}
        </div>
      </div>

      <div className="saved-views mb-16">
        <span className="faint" style={{ fontSize: 11.5, textTransform: "uppercase", letterSpacing: ".1em", fontFamily: "var(--mono)" }}>Views</span>
        {views.map((v) => (
          <span key={v.id} className={`view-pill ${activeView === v.id ? "view-pill--on" : ""}`}>
            <button onClick={() => applyView(v)}>{v.name}</button>
            <button className="view-pill__x" title="Delete view" onClick={() => deleteView(v.id)}>×</button>
          </span>
        ))}
        <button className="view-pill view-pill--add" onClick={saveCurrentView}><Icon name="plus" size={12} /> Save current</button>
      </div>

      <div className="row between mb-16">
        <span className="muted" style={{ fontSize: 13 }}>
          <b style={{ color: "var(--ink)" }}>{rows.length}</b> {rows.length === 1 ? "account" : "accounts"}
        </span>
        <span className="muted" style={{ fontSize: 13 }}>
          Total ARR <b className="num serif" style={{ color: "var(--ink)", fontSize: 15 }}>{usd(totalArr)}</b>
        </span>
      </div>

      <div className="card">
        <div style={{ overflowX: "auto" }}>
          <table className="table">
            <thead>
              <tr>
                <th>Account</th>
                <th>Owner</th>
                <th>Health</th>
                <th>Stage</th>
                <th className="num">ARR</th>
                <th className="num">Seats</th>
                <th>Renewal</th>
                <th className="num">Signals</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((a) => {
                const owner = userById(a.ownerId);
                return (
                  <tr key={a.id} onClick={() => navigate(`/accounts/${a.id}`)}>
                    <td>
                      <div className="acct-cell">
                        <Monogram name={a.name} />
                        <div>
                          <b>{a.name}</b>
                          <small>{a.domain}</small>
                        </div>
                      </div>
                    </td>
                    <td>
                      {owner ? (
                        <div className="row" style={{ gap: 9 }}>
                          <Avatar name={owner.name} color={owner.color} size="sm" />
                          <span style={{ fontSize: 13 }}>{owner.name}</span>
                        </div>
                      ) : (
                        <span className="faint">—</span>
                      )}
                    </td>
                    <td>
                      <div className="row" style={{ gap: 10 }}>
                        <HealthRing value={a.health} size={34} stroke={4} />
                        <Sparkline data={a.healthTrend} band={a.healthBand} width={72} height={24} />
                      </div>
                    </td>
                    <td><span className="stage">{a.lifecycleStage}</span></td>
                    <td className="num">{usd(a.arr)}</td>
                    <td className="num">
                      {num(a.seatsActive)}<span className="faint"> / {num(a.seats)}</span>
                    </td>
                    <td>
                      <div>
                        <span style={{ fontSize: 13 }}>{relDate(a.renewalDate)}</span>
                        <small className="faint" style={{ display: "block", fontSize: 11.5 }}>{shortDate(a.renewalDate)}</small>
                      </div>
                    </td>
                    <td className="num">
                      <span className="tag">{a.openSignals}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {rows.length === 0 && (
          <div className="empty">No accounts match these filters.</div>
        )}
      </div>
    </div>
  );
}
